import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setAllUsers } from '../../../../../Reducers/allUsersReducer';
import { deleteAssessmentSubmission, getAllUsers, getAllUsersOfACourse, getAssementByIdAndRole, getSubmissionsByAssestment } from '../../../../../Constants/Api/functions/function';
import { getGradeDetails } from '../../../../../Utils/gradeUtils';

// Antd
import {
    Button,
    Space,
    Input,
    Table,
    Tooltip,
    Popconfirm,
    notification,
    Tag,
    Card,
    Avatar,
    Segmented,
    Empty
} from 'antd';
import Highlighter from 'react-highlight-words';
import {
    SearchOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    EditOutlined,
    ExportOutlined,
    UserOutlined,
    TrophyOutlined,
    FileTextOutlined
} from '@ant-design/icons';

import './style.scss';
import ReviewSubmission from './ReviewSubmission';
import UpdateReviewSubmission from './UpdateReviewSubmission';

const Submission = ({ assessmentId, assessmentData }) => {
    const [loading, setLoading] = useState(false);
    const [submissions, setSubmissions] = useState([]);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [isOpenReview, setIsOpenReview] = useState(false);
    const [isOpenUpdateReview, setIsOpenUpdateReview] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [api, contextHolder] = notification.useNotification();
    const userProfile = useSelector(state => state.userProfile.userProfile);
    const allUsers = useSelector(state => state.allUsers.allUsers);
    const [students, setStudents] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);
    const dispatch = useDispatch();

    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth <= 768;
        }
        return false;
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        handleGetSubmissions();
    }, [assessmentId]);

    useEffect(() => {
        if (!allUsers || allUsers.length === 0) {
            handleGetAllUsers();
        }
    }, [allUsers]);

    // Fetch students actually enrolled in this specific assessment's course
    useEffect(() => {
        const fetchEnrolledCourseStudents = async () => {
            let targetCourseId = assessmentData?.courseId;
            if (!targetCourseId && assessmentId && userProfile) {
                try {
                    const ass = await getAssementByIdAndRole(assessmentId, userProfile.role, userProfile.id);
                    targetCourseId = ass?.courseId;
                } catch (e) {
                    console.error('Error fetching assessment courseId in Submission:', e);
                }
            }

            if (targetCourseId) {
                try {
                    const courseUsers = await getAllUsersOfACourse(targetCourseId);
                    if (courseUsers && Array.isArray(courseUsers)) {
                        const enrolledStudents = courseUsers.filter(u => u.role === 'student');
                        setStudents(enrolledStudents.length > 0 ? enrolledStudents : courseUsers);
                    }
                } catch (err) {
                    console.error('Error fetching enrolled students for course:', err);
                }
            }
        };

        fetchEnrolledCourseStudents();
    }, [assessmentData?.courseId, assessmentId, userProfile?.id, userProfile?.role]);

    const handleGetAllUsers = async () => {
        try {
            const response = await getAllUsers();
            if (response) {
                dispatch(setAllUsers(response));
            }
        } catch (error) {
            console.error('getAllUsers', error);
        }
    };

    const handleGetSubmissions = async () => {
        setLoading(true);
        try {
            const response = await getSubmissionsByAssestment(assessmentId);
            setSubmissions(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('getSubmissionsByAssestment', error);
        }
        setLoading(false);
    };

    // Calculate Summary Statistics for this Assessment
    const stats = useMemo(() => {
        const total = submissions.length;
        const reviewed = submissions.filter(s => s.isReviewed);
        const pending = submissions.filter(s => !s.isReviewed);
        const reviewedCount = reviewed.length;
        const pendingCount = pending.length;

        const gradedWithMarks = reviewed.filter(s => s.marks !== null && s.marks !== undefined && s.marks !== '' && !isNaN(Number(s.marks)));
        let averageMarks = null;
        let averageGrade = null;

        if (gradedWithMarks.length > 0) {
            const sum = gradedWithMarks.reduce((acc, curr) => acc + Number(curr.marks), 0);
            averageMarks = Math.round((sum / gradedWithMarks.length) * 10) / 10;
            averageGrade = getGradeDetails(averageMarks);
        }

        return {
            total,
            reviewedCount,
            pendingCount,
            averageMarks,
            averageGrade,
        };
    }, [submissions]);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => close()}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const handleDeleteSubmission = async (submissionId) => {
        setLoading(true);
        try {
            const response = await deleteAssessmentSubmission(submissionId);
            if (response && (response.status === 200 || response.success)) {
                await handleGetSubmissions();
                openNotification('success', 'Submission Deleted', 'The submission has been deleted successfully.');
            } else {
                openNotification('error', 'Error', 'An error occurred while deleting the submission.');
            }
        } catch (error) {
            console.error('deleteSubmission', error);
            openNotification('error', 'Error', 'An error occurred while deleting the submission.');
        }
        setLoading(false);
    };

    const openNotification = (type, message, description) => {
        api[type]({
            message: message,
            description: description,
        });
    };

    // Filter table data by status tab (all / pending / reviewed)
    const filteredSubmissions = useMemo(() => {
        let list = submissions;
        if (statusFilter === 'pending') {
            list = submissions.filter(s => !s.isReviewed);
        } else if (statusFilter === 'reviewed') {
            list = submissions.filter(s => s.isReviewed);
        }
        return list.map((submission) => {
            const user = allUsers?.find(u => String(u.id) === String(submission.userId));
            const reviewer = allUsers?.find(u => String(u.id) === String(submission.reviewdBy || submission.reviewedBy));
            return {
                ...submission,
                key: submission.id,
                studentName: submission.name || user?.name || 'Student',
                studentEmail: submission.email || user?.email || '',
                reviewerName: reviewer?.name || 'Instructor',
            };
        });
    }, [submissions, allUsers, statusFilter]);

    const columns = [
        {
            title: 'Student',
            dataIndex: 'studentName',
            key: 'studentName',
            width: '26%',
            ...getColumnSearchProps('studentName'),
            render: (name, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar
                        style={{ backgroundColor: '#2563eb', flexShrink: 0 }}
                        icon={<UserOutlined />}
                    >
                        {name ? name.charAt(0).toUpperCase() : 'S'}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {name}
                        </div>
                        {record.studentEmail && (
                            <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {record.studentEmail}
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: 'Submission Status',
            dataIndex: 'isReviewed',
            key: 'isReviewed',
            width: '16%',
            render: (isReviewed, record) => {
                if (isReviewed) {
                    return (
                        <Tooltip title={`Reviewed by: ${record.reviewerName}`}>
                            <Tag color="success" icon={<CheckCircleOutlined />} style={{ padding: '3px 8px', fontWeight: 600, cursor: 'pointer' }}>
                                Reviewed / Graded
                            </Tag>
                        </Tooltip>
                    );
                }
                return (
                    <Tag color="warning" icon={<ClockCircleOutlined />} style={{ padding: '3px 8px', fontWeight: 600 }}>
                        Pending Review
                    </Tag>
                );
            },
        },
        {
            title: 'Marks & Grade',
            dataIndex: 'marks',
            key: 'marks',
            width: '22%',
            sorter: (a, b) => (Number(a.marks) || 0) - (Number(b.marks) || 0),
            render: (marks, record) => {
                if (!record.isReviewed || marks === null || marks === undefined || marks === '' || marks === 'N/A') {
                    return (
                        <Tag color="default" style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                            Pending Evaluation
                        </Tag>
                    );
                }
                const gradeInfo = getGradeDetails(marks);
                return (
                    <Space size={6} wrap>
                        <strong style={{ fontSize: 15, color: '#0f172a' }}>{marks}%</strong>
                        <Tag color={gradeInfo.tagColor} style={{ fontWeight: 600, fontSize: 12 }}>
                            Grade {gradeInfo.grade} • {gradeInfo.classification}
                        </Tag>
                    </Space>
                );
            },
        },
        {
            title: 'Submitted At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: '18%',
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            render: (date, record) => (
                <div>
                    <div style={{ fontSize: 13, color: '#334155' }}>
                        {date ? new Date(date).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                    </div>
                    {record.attemptNumber && (
                        <Tag color="cyan" style={{ fontSize: 11, marginTop: 2 }}>
                            Attempt #{record.attemptNumber}
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'action',
            width: '18%',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {record.submissionUrl && (
                        <Tooltip title="Open student submitted work in new tab">
                            <Button
                                size="small"
                                icon={<ExportOutlined />}
                                href={record.submissionUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Work
                            </Button>
                        </Tooltip>
                    )}

                    {['superadmin', 'admin', 'instructor'].includes(userProfile?.role) && (
                        <>
                            {record.isReviewed ? (
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        setSelectedSubmission(submissions.find(s => s.id === record.id));
                                        setIsOpenUpdateReview(true);
                                    }}
                                >
                                    Update Grade
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => {
                                        setSelectedSubmission(submissions.find(s => s.id === record.id));
                                        setIsOpenReview(true);
                                    }}
                                >
                                    Review & Grade
                                </Button>
                            )}

                            <Popconfirm
                                title="Are you sure to delete this student submission?"
                                onConfirm={() => handleDeleteSubmission(record.id)}
                                okText="Yes, Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    danger
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                />
                            </Popconfirm>
                        </>
                    )}
                </div>
            ),
        }
    ];

    return (
        <div className='submissions-management'>
            {contextHolder}

            {/* Top KPI Metric Cards for Assignment Details */}
            <div className='submissions-kpi-grid'>
                <Card size="small" className='kpi-card' style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div className='kpi-label' style={{ color: '#64748b' }}>
                        Total Submissions
                    </div>
                    <div className='kpi-value' style={{ color: '#0f172a' }}>
                        {stats.total}
                        {(students.length > 0 || assessmentData?.enrolledStudents !== undefined) && (
                            <span className='kpi-subtext'>
                                {' '}/ {students.length || assessmentData?.enrolledStudents || 0} enrolled
                            </span>
                        )}
                    </div>
                </Card>

                <Card size="small" className='kpi-card' style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div className='kpi-label' style={{ color: '#166534' }}>
                        Graded & Reviewed
                    </div>
                    <div className='kpi-value' style={{ color: '#15803d' }}>
                        {stats.reviewedCount}
                    </div>
                </Card>

                <Card size="small" className='kpi-card' style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                    <div className='kpi-label' style={{ color: '#92400e' }}>
                        Pending Review
                    </div>
                    <div className='kpi-value' style={{ color: '#b45309' }}>
                        {stats.pendingCount}
                    </div>
                </Card>

                <Card size="small" className='kpi-card' style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <div className='kpi-label' style={{ color: '#1e40af' }}>
                        Class Average
                    </div>
                    <div className='kpi-value' style={{ color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {stats.averageGrade ? (
                            <>
                                <TrophyOutlined style={{ color: stats.averageGrade.color }} />
                                <span>{stats.averageMarks}% ({stats.averageGrade.grade})</span>
                            </>
                        ) : (
                            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>No grades yet</span>
                        )}
                    </div>
                </Card>
            </div>

            {/* Submissions List Header & Controls */}
            <div className='submissions-header-controls'>
                <div className='submissions-title-row'>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                            Student Submissions
                        </h3>
                        <Tag color="geekblue" style={{ fontSize: 12, padding: '1px 8px', borderRadius: 6, margin: 0 }}>
                            {stats.total} Total
                        </Tag>
                    </div>
                </div>

                <div className='submissions-filter-segmented'>
                    <Segmented
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                            { label: `All (${stats.total})`, value: 'all' },
                            { label: `Pending (${stats.pendingCount})`, value: 'pending' },
                            { label: `Reviewed (${stats.reviewedCount})`, value: 'reviewed' },
                        ]}
                    />
                </div>
            </div>

            {/* Submissions Desktop Table */}
            {!isMobile && (
                <div className='submissions__desktop-table'>
                    <Table
                        columns={columns}
                        dataSource={filteredSubmissions}
                        loading={loading}
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            position: ['bottomRight']
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        statusFilter === 'pending'
                                            ? "No submissions currently pending review."
                                            : statusFilter === 'reviewed'
                                                ? "No graded submissions yet."
                                                : "No students have submitted this assignment yet."
                                    }
                                />
                            )
                        }}
                        scroll={{ x: 800 }}
                    />
                </div>
            )}

            {/* Submissions Mobile Cards */}
            {isMobile && (
                <div className='submissions__mobile-cards'>
                    {filteredSubmissions.length === 0 ? (
                        <Card style={{ borderRadius: 10, textAlign: 'center', padding: 24 }}>
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    statusFilter === 'pending'
                                        ? "No submissions currently pending review."
                                        : statusFilter === 'reviewed'
                                            ? "No graded submissions yet."
                                            : "No students have submitted this assignment yet."
                                }
                            />
                        </Card>
                    ) : (
                        filteredSubmissions.map((record) => {
                            const gradeInfo = record.marks && record.isReviewed ? getGradeDetails(record.marks) : null;
                            const dateFormatted = record.createdAt
                                ? new Date(record.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : 'N/A';

                            return (
                                <div
                                    key={record.id}
                                    className={`mobile-submission-card ${record.isReviewed ? 'reviewed' : 'pending'}`}
                                >
                                    <div className='card-header-row'>
                                        <div className='student-profile'>
                                            <Avatar
                                                src={record.user?.profilePicture}
                                                icon={!record.user?.profilePicture && <UserOutlined />}
                                                className='student-avatar'
                                                size={38}
                                            />
                                            <div className='student-meta'>
                                                <span className='student-name'>
                                                    {record.studentName || `${record.user?.firstName || 'Student'} ${record.user?.lastName || ''}`}
                                                </span>
                                                <span className='student-email'>
                                                    {record.studentEmail || record.user?.email || '-'}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            {record.isReviewed ? (
                                                <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0, fontWeight: 600 }}>
                                                    Reviewed
                                                </Tag>
                                            ) : (
                                                <Tag color="warning" icon={<ClockCircleOutlined />} style={{ margin: 0, fontWeight: 600 }}>
                                                    Pending Review
                                                </Tag>
                                            )}
                                        </div>
                                    </div>

                                    <div className='card-body-grid'>
                                        <div className='card-field'>
                                            <span className='field-label'>Submitted At</span>
                                            <span className='field-value'>{dateFormatted}</span>
                                        </div>

                                        <div className='card-field'>
                                            <span className='field-label'>Attempt</span>
                                            <span className='field-value'>
                                                <Tag color="cyan" style={{ margin: 0, fontSize: 11 }}>
                                                    Attempt #{record.attemptNumber || 1}
                                                </Tag>
                                            </span>
                                        </div>

                                        <div className='card-field card-field-full'>
                                            <span className='field-label'>Marks & Grade</span>
                                            <span className='field-value'>
                                                {!record.isReviewed || record.marks === null || record.marks === undefined || record.marks === '' ? (
                                                    <Tag color="default" style={{ margin: 0 }}>Pending Evaluation</Tag>
                                                ) : (
                                                    <Space size={6} wrap>
                                                        <strong style={{ fontSize: 15, color: '#0f172a' }}>{record.marks}%</strong>
                                                        {gradeInfo && (
                                                            <Tag color={gradeInfo.tagColor} style={{ fontWeight: 600, margin: 0 }}>
                                                                Grade {gradeInfo.grade} • {gradeInfo.classification}
                                                            </Tag>
                                                        )}
                                                    </Space>
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className='card-actions-row'>
                                        {record.submissionUrl && (
                                            <Button
                                                size="middle"
                                                className='action-btn-secondary'
                                                icon={<ExportOutlined />}
                                                href={record.submissionUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Work
                                            </Button>
                                        )}

                                        {['superadmin', 'admin', 'instructor'].includes(userProfile?.role) && (
                                            <>
                                                {record.isReviewed ? (
                                                    <Button
                                                        size="middle"
                                                        className='action-btn-primary'
                                                        icon={<EditOutlined />}
                                                        onClick={() => {
                                                            setSelectedSubmission(submissions.find(s => s.id === record.id));
                                                            setIsOpenUpdateReview(true);
                                                        }}
                                                    >
                                                        Update Grade
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        type="primary"
                                                        size="middle"
                                                        className='action-btn-primary'
                                                        icon={<CheckCircleOutlined />}
                                                        onClick={() => {
                                                            setSelectedSubmission(submissions.find(s => s.id === record.id));
                                                            setIsOpenReview(true);
                                                        }}
                                                    >
                                                        Review & Grade
                                                    </Button>
                                                )}

                                                <Popconfirm
                                                    title="Are you sure to delete this student submission?"
                                                    onConfirm={() => handleDeleteSubmission(record.id)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                    okButtonProps={{ danger: true }}
                                                >
                                                    <Button
                                                        danger
                                                        size="middle"
                                                        className='action-btn-secondary'
                                                        icon={<DeleteOutlined />}
                                                    />
                                                </Popconfirm>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Review Drawers */}
            <ReviewSubmission
                isOpen={isOpenReview}
                setOpen={setIsOpenReview}
                handleGetSubmissions={handleGetSubmissions}
                submissionData={selectedSubmission}
                userData={allUsers?.find(user => String(user?.id) === String(selectedSubmission?.userId))}
                assessmentData={assessmentData}
            />

            <UpdateReviewSubmission
                isOpen={isOpenUpdateReview}
                setOpen={setIsOpenUpdateReview}
                handleGetSubmissions={handleGetSubmissions}
                submissionData={selectedSubmission}
                userData={allUsers?.find(user => String(user?.id) === String(selectedSubmission?.userId))}
            />
        </div>
    );
};

export default Submission;